import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModelCommentBodyForm } from '../ModelCommentBodyForm';
import { Button } from '@framework/ui/atoms/Button';
import { usePopupAvailable } from '@view-model/models/common/hooks';

type Props = {
  onReplyCommentSubmit(body: string): void;
};

export const ModelCommentReply: React.FC<Props> = ({ onReplyCommentSubmit }: Props) => {
  const { t } = useTranslation();
  const [{ isEditing, body }, setState] = useState({ isEditing: false, body: '' });
  const reset = useCallback(() => setState({ isEditing: false, body: '' }), []);

  usePopupAvailable(body.length > 0);

  const handleReplyEditing = useCallback(() => {
    setState((s) => ({ ...s, isEditing: true }));
  }, []);

  const handleSubmit = useCallback(() => {
    onReplyCommentSubmit(body);
    reset();
  }, [body, onReplyCommentSubmit, reset]);

  const handleCancel = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <>
      <ModelCommentBodyForm
        autoFocus={false}
        body={body}
        ariaLabel={t('canvas.comment.reply.placeholder')}
        onChange={(body) => setState((s) => ({ ...s, body }))}
        onSubmit={handleSubmit}
        placeholder={t('canvas.comment.reply.placeholder')}
        onFocus={handleReplyEditing}
        minRows={2}
      />
      {isEditing && (
        <div className="flex justify-end gap-2">
          <Button color="mono" className="rounded-lg p-2 text-3xl" onClick={handleCancel}>
            {t('common.action.cancel')}
          </Button>
          <Button color="brand" className="rounded-lg p-2 text-3xl" onClick={handleSubmit} disabled={!body}>
            {t('common.action.send')}
          </Button>
        </div>
      )}
    </>
  );
};
